import React, { useEffect, useRef, useState } from 'react'
import CameraControls from './CameraControls'
import Loading from 'components/Loading'

const CODECS = [
  'avc1.640029',
  'avc1.64002A',
  'avc1.640033',
  'hvc1.1.6.L153.B0',
  'mp4a.40.2',
  'mp4a.40.5',
  'flac',
  'opus',
]


interface PlayerMseProps {
  url: string
}

/**
 * Filtruje kodeki nieobsługiwane przez daną wersję Safari.
 * Safari kłamie w MediaSource.isTypeSupported dla audio,
 * dlatego trzeba ręcznie obciąć listę na podstawie user agenta.
 */
function getSafariCompatibleCodecs() {
  const m = window.navigator.userAgent.match(/Version\/(\d+).+Safari/)
  if (!m) return CODECS

  const version = parseInt(m[1], 10)
  if (version < 13) return CODECS.slice(0, CODECS.indexOf('mp4a.40.2'))
  if (version < 14) return CODECS.slice(0, CODECS.indexOf('flac'))
  return CODECS.slice(0, CODECS.indexOf('opus'))
}

const PlayerMse: React.FC<PlayerMseProps> = ({ url }) => {
  const [isLoading, setLoading] = useState(true)

  const wsRef = useRef<WebSocket | null>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const objectUrlRef = useRef<string | null>(null)
  const mediaSourceRef = useRef<MediaSource | null>(null)
  const sourceBufferRef = useRef<SourceBuffer | null>(null)
  const reconnectTimeout = useRef<basic.SetTimeout | null>(null)
  const bufferRef = useRef(new Uint8Array(2 * 1024 * 1024))
  const bufferLengthRef = useRef(0)

  const play = () => {
    const video = videoRef.current
    if (!video) return

    video
      .play()
      .then(() => {
        setLoading(false)
      })
      .catch(() => {
        if (!video.muted) {
          video.muted = true
          video.play().catch(() => { })
        }
      })
  }

  const getCodecs = (isSupported: (type: string) => boolean) => {
    const compatibleCodecs = getSafariCompatibleCodecs()
    return compatibleCodecs
      .filter((codec) => isSupported(`video/mp4 codecs='${codec}'`))
      .join()
  }

  const showError = (msg: string) => {
    console.error('[MSEPlayer]', msg)
  }

  const setupMSE = (ws: WebSocket) => {
    const video = videoRef.current
    if (!video) return

    let ms

    if ('ManagedMediaSource' in window) {
      const MediaSource = window.ManagedMediaSource

      ms = new MediaSource()
      ms.addEventListener(
        'sourceopen',
        () => {
          ws.send(
            JSON.stringify({
              type: 'mse',
              value: getCodecs(MediaSource.isTypeSupported),
            })
          )
        },
        { once: true }
      )

      video.disableRemotePlayback = true
      video.srcObject = ms
    } else {
      ms = new MediaSource()

      ms.addEventListener(
        'sourceopen',
        () => {
          if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current)
            objectUrlRef.current = null
          }
          ws.send(
            JSON.stringify({
              type: 'mse',
              value: getCodecs(MediaSource.isTypeSupported),
            })
          )
        },
        { once: true }
      )

      const objectUrl = URL.createObjectURL(ms)
      objectUrlRef.current = objectUrl
      video.srcObject = null
      video.src = objectUrl
    }

    mediaSourceRef.current = ms

    play()
  }

  const handleMessage = (msg: any) => {
    if (msg.type !== 'mse') return

    try {
      const ms = mediaSourceRef.current
      const video = videoRef.current
      if (!ms) return

      const sb = ms.addSourceBuffer(msg.value)

      try {
        sb.mode = 'segments'
      } catch (e) {
        console.warn('[MSEPlayer] Cannot set segments mode:', e)
      }

      sourceBufferRef.current = sb

      sb.addEventListener('error', () => {
        showError('SourceBuffer error')
      })

      sb.addEventListener('updateend', () => {
        if (!ms || ms.readyState !== 'open' || !sb || sb.updating) return

        const buffer = bufferRef.current
        const bufferLen = bufferLengthRef.current

        if (bufferLen > 0) {
          const data = buffer.slice(0, bufferLen)
          try {
            sb.appendBuffer(data)
          } catch (e) { }
          bufferLengthRef.current = 0
        }

        if (sb.buffered.length) {
          const end = sb.buffered.end(sb.buffered.length - 1)
          const start = end - 5
          const start0 = sb.buffered.start(0)

          if (start > start0) {
            try {
              sb.remove(start0, start)
              mediaSourceRef.current?.setLiveSeekableRange(start, end)
            } catch (e) { }
          }

          if (video) {
            if (video.currentTime < start) {
              video.currentTime = start
            }

            const gap = end - video.currentTime
            video.playbackRate = gap > 0.1 ? gap : 0.1
          }
        }
      })
    } catch (e) {
      showError('MSE initialization error')
    }
  }

  const handleBinary = (data: any) => {
    const sb = sourceBufferRef.current
    if (!sb) return

    const buffer = bufferRef.current
    const bufferLen = bufferLengthRef.current

    if (sb.updating || bufferLen > 0) {
      const chunk = new Uint8Array(data)
      buffer.set(chunk, bufferLen)
      bufferLengthRef.current += chunk.byteLength
    } else {
      try {
        sb.appendBuffer(data)
      } catch {
        showError('Buffer append error')
      }
    }
  }

  useEffect(() => {
    let isMounted = true
    const video = videoRef.current
    video?.addEventListener('error', () => {
      if (!isMounted) return
      const err = video.error
      const code = err?.code

      if (code === 1 || code === 2 || code === 3 || code === 4) {
        const MEDIA_ERRORS = {
          1: 'MEDIA_ERR_ABORTED',
          2: 'MEDIA_ERR_NETWORK',
          3: 'MEDIA_ERR_DECODE',
          4: 'MEDIA_ERR_SRC_NOT_SUPPORTED',
        }
        showError(MEDIA_ERRORS[code])
      } else {
        showError('Video playback error')
      }
    })

    const connect = () => {
      if (!url) return
      setLoading(true)

      const ws = new WebSocket(url)
      ws.binaryType = 'arraybuffer'
      wsRef.current = ws

      ws.onopen = () => {
        setupMSE(ws)
      }

      ws.onerror = () => {
        showError('WebSocket connection error')
      }

      ws.onclose = () => {
        if (!isMounted) return
        reconnectTimeout.current = setTimeout(connect, 3000)
      }

      ws.onmessage = (ev) => {
        if (typeof ev.data === 'string') {
          const msg = JSON.parse(ev.data)
          handleMessage(msg)
        } else {
          handleBinary(ev.data)
        }
      }
    }

    connect()

    return () => {
      isMounted = false
      if (wsRef.current) wsRef.current.close()
      if (reconnectTimeout.current) clearTimeout(reconnectTimeout.current)

      if (video) {
        video.src = ''
        video.srcObject = null
      }

      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current)
        objectUrlRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url])

  return (
    <div className='cam__wrapper'>
      <video
        muted
        autoPlay
        controls
        playsInline
        ref={videoRef}
      />
      <CameraControls
        url={url}
        type='mse'
      />
      {isLoading && <Loading middleOfPage />}
    </div>
  )
}

export default PlayerMse